import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAdminLoggedIn, login } from '../services/adminAuth';
import './Admin.css';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (isAdminLoggedIn()) navigate('/admin/dashboard', { replace: true });
  }, [navigate]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.username.trim()) e.username = 'Email or username is required';
    if (!form.password) e.password = 'Password is required';
    else if (form.password.length < 3) e.password = 'Password must be at least 3 characters';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setApiError('');
    setLoading(true);
    try {
      await login(form.username.trim(), form.password);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setApiError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-root">
      <div className="admin-login-screen">
        <div className="admin-login-card">
          <div className="admin-login-header">
            <img
              className="admin-login-logo"
              src={`${process.env.PUBLIC_URL}/assets/pharmvetpat-logo.svg`}
              alt="PharmVetPat"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1>Admin Panel</h1>
            <p>ChemRobotics Chatbot Administration</p>
          </div>

          <div className="admin-login-body">
            {apiError && (
              <div className="admin-login-error">
                <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                  <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" clipRule="evenodd" />
                </svg>
                {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="adm-username">
                  Email / Username <span>*</span>
                </label>
                <input
                  id="adm-username"
                  className={`admin-form-input${errors.username ? ' is-error' : ''}`}
                  type="text"
                  autoComplete="email"
                  placeholder="Enter email or username"
                  value={form.username}
                  onChange={set('username')}
                  disabled={loading}
                />
                {errors.username && (
                  <div className="admin-form-error">
                    <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 3.5a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4.5zm0 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/></svg>
                    {errors.username}
                  </div>
                )}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="adm-password">
                  Password <span>*</span>
                </label>
                <div className="admin-input-wrap">
                  <input
                    id="adm-password"
                    className={`admin-form-input${errors.password ? ' is-error' : ''}`}
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter password"
                    value={form.password}
                    onChange={set('password')}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="admin-input-wrap__btn"
                    onClick={() => setShowPwd((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="17" height="17">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <div className="admin-form-error">
                    <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12"><path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 3.5a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 8 4.5zm0 7a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/></svg>
                    {errors.password}
                  </div>
                )}
              </div>

              <button type="submit" className="admin-login-btn" disabled={loading}>
                {loading && <span className="admin-spinner" />}
                {loading ? 'Signing in…' : 'Sign In to Admin'}
              </button>
            </form>

            <div className="admin-login-back">
              <a href="/login">← Back to User Login</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
